// PICK TILES FOR A SIGN-OFF (owner 2026-11-01: "an easy way to select specific tiles and their
// systems, then fine tune with specific tiles, addition, subtraction, so I can request them for
// sign-off"). Start from any level, area, system or 3D volume (its tiles are added), add or take
// out single tiles by clicking them in the model or tile view (the mode below says which), add
// or take out everything shown by the page's level / area / volume filters, then pick the
// systems inside the selection. "Request sign-off" turns the selection into a one-off tile
// selection (a hidden 3D volume, back/models/signoffVolume.adhoc) and opens the request window
// on it - one target per system picked, or the whole selection.
import React, { useMemo, useState } from "react";
import { Button, Checkbox, Segmented, Select, Space, Tag } from "antd";
import { createVolume } from "api/signoff";
import addNotification from "helpers/notify";
import { tilesOf } from "./signoffState";

/** The systems in some tiles, with how many elements of each: [{ system, n }], most first. */
export function systemsIn(tiles) {
  const n = new Map();
  for (const t of tiles || []) {
    for (const g of [...(t.g || []), ...(t.ug || [])]) for (const s of g.s || []) n.set(s, (n.get(s) || 0) + (g.n || 0));
  }
  return [...n.entries()].map(([system, c]) => ({ system, n: c })).sort((a, b) => b.n - a.n || a.system.localeCompare(b.system));
}

/** The request targets for a selection: one per system picked, or the whole selection. */
export function selectionTargets(volumeId, systems) {
  return systems && systems.length ? systems.map((system) => ({ kind: "volume", volumeId, system })) : [{ kind: "volume", volumeId }];
}

export default function TilePicker({ projectId, list, picked, setPicked, mode, setMode, systems, setSystems, touching = null, visible, areas, levels, volumes, volsOf, onRequest, onClose }) {
  const [busy, setBusy] = useState(false);
  const byKey = useMemo(() => new Map((list || []).map((t) => [t.key, t])), [list]);
  const pickedTiles = useMemo(() => [...picked].map((k) => byKey.get(k)).filter(Boolean), [picked, byKey]);
  const found = useMemo(() => systemsIn(pickedTiles), [pickedTiles]);
  const elements = pickedTiles.reduce((s, t) => s + (t.n || 0), 0);

  // where to start from: the same choices as the request window
  const starts = useMemo(() => {
    const out = [];
    Object.entries(levels || {}).forEach(([k, name]) => out.push({ value: `level|${k}`, label: `Level · ${name}`, t: { kind: "level", levelKey: k, level: name } }));
    Object.entries(areas || {}).filter(([, a]) => a.current).forEach(([id, a]) => {
      out.push({ value: `area|${id}`, label: `Area · ${[a.name, a.level].filter(Boolean).join(" · ")}`, t: { kind: "area", areaSetId: id } });
      (a.systems || []).forEach((sys) => out.push({ value: `system|${id}|${sys}`, label: `${sys} · ${a.name}`, t: { kind: "system", areaSetId: id, system: sys } }));
    });
    (volumes || []).filter((v) => !v.adhoc && v.tiles && v.tiles.length).forEach((v) => out.push({ value: `volume|${v.id}`, label: `3D volume · ${v.name}`, t: { kind: "volume", volumeId: v.id } }));
    return out.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [areas, levels, volumes]);

  const addKeys = (keys) => setPicked((p) => { const n = new Set(p); keys.forEach((k) => n.add(k)); return n; });
  const removeKeys = (keys) => setPicked((p) => { const n = new Set(p); keys.forEach((k) => n.delete(k)); return n; });
  const shown = () => (list || []).filter((t) => visible(t)).map((t) => t.key);

  const request = async () => {
    setBusy(true);
    try {
      const keys = [...picked];
      const { volume } = await createVolume(projectId, { name: `Tile selection (${keys.length} tile${keys.length === 1 ? "" : "s"})`, tiles: keys, adhoc: true });
      onRequest(volume, selectionTargets(volume.id, systems));
    } catch (e) {
      addNotification(e.message, "danger");
    }
    setBusy(false);
  };

  return (
    <div className="signoff-box tile-picker" data-testid="tile-picker">
      <div className="signoff-box-title d-flex justify-content-between">
        <span>Select tiles for a sign-off</span>
        <Button size="small" type="text" onClick={onClose}>Close</Button>
      </div>
      <small className="signoff-muted d-block mb-1">Start from</small>
      <Select showSearch placeholder="+ a level, area, system or 3D volume" style={{ width: "100%" }} value={null} optionFilterProp="label" options={starts}
              onChange={(v) => { const o = starts.find((x) => x.value === v); if (o) addKeys(tilesOf(list, o.t, volsOf).map((t) => t.key)); }} />
      <small className="signoff-muted d-block mt-2 mb-1">Click a tile in the view to</small>
      <Segmented block value={mode} onChange={setMode} options={[{ value: "add", label: "Add" }, { value: "remove", label: "Take out" }]} />
      <Space wrap size={4} className="mt-2">
        <Button size="small" onClick={() => addKeys(shown())}>Add all shown</Button>
        <Button size="small" onClick={() => removeKeys(shown())}>Take out all shown</Button>
        <Button size="small" danger disabled={!picked.size} onClick={() => setPicked(new Set())}>Clear</Button>
      </Space>
      <div className="tile-row mt-2"><span>Selected</span><b>{picked.size.toLocaleString()} tiles · {(touching ? touching.total : elements).toLocaleString()} elements{touching ? " inside or crossing" : ""}</b></div>
      {found.length ? (
        <>
          <small className="signoff-muted d-block mt-2">Systems to sign off <span className="signoff-muted">(none ticked = every element in the tiles)</span></small>
          <div className="tile-picker-systems">
            {found.map((f) => (
              <Checkbox key={f.system} checked={systems.includes(f.system)}
                        onChange={(e) => setSystems(e.target.checked ? [...systems, f.system] : systems.filter((s) => s !== f.system))}>
                {f.system} <Tag className="ml-1">{f.n.toLocaleString()}</Tag>
              </Checkbox>
            ))}
          </div>
        </>
      ) : picked.size ? <small className="signoff-muted d-block mt-2">No system recorded in these tiles - the sign-off covers every element in them.</small> : null}
      <Button className="mt-3" type="primary" block disabled={!picked.size} loading={busy} onClick={request} data-testid="tile-picker-request">
        Request sign-off for {picked.size.toLocaleString()} tile{picked.size === 1 ? "" : "s"}{systems.length ? ` · ${systems.length} system${systems.length === 1 ? "" : "s"}` : ""}
      </Button>
    </div>
  );
}
